import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { COLORS } from '../styles/theme';
import React, { useEffect, useState } from 'react';
import { STORAGE_CURRENT_CASE_ID } from '../constants';
import {
  useAgentExtension,
  useNotification,
  useLocalStorage,
  useUser,
  useAuthentication,
  useCase,
} from '../hooks';
import { SnackBarNotificationOptions } from '../types/SnackBarNotificationOptions';
import { ExtensionDialog } from './app/ExtensionDialog';

import moment from 'moment';

const StyledButtonPrimary = styled(Button)(({}) => ({
  color: COLORS.WHITE,
  backgroundColor: COLORS.PRIMARY_BLUE,
  minWidth: '80px',
  float: 'right',
  '&:hover': {
    backgroundColor: COLORS.PRIMARY_BLUE,
  },
}));

export const AgentExtensionActivity = (): React.ReactElement => {
  const { agentExtensionLogsData } = useAgentExtension();
  const { showNotification } = useNotification();
  const { withLocalStorage } = useLocalStorage();
  const { userExtension, userProfile } = useUser();
  const { auth } = useAuthentication();
  const { caseData } = useCase();

  const isOpen = true;
  const [caseId, setCaseId] = useState<string>('');
  const [isOpenExtensionDialog, setIsOpenExtensionDialog] =
    useState<boolean>(false);
  const [extensionDialogCase, setExtensionDialogCase] = useState<string>('');

  const [displayDateTimeModification, setTimeModification] = useState<any>({
    checkOutTime: '',
    releaseTime: '',
  });

  const closeExtensionDialog = () => {
    setIsOpenExtensionDialog(false);
  };

  useEffect(() => {
    if (agentExtensionLogsData) {
      if (
        agentExtensionLogsData.check_out_time &&
        agentExtensionLogsData.release_time
      ) {
        const check_out_time = moment(
          new Date(agentExtensionLogsData.check_out_time),
        )
          .utc()
          .format('YYYY-MM-DD HH:mm');

        const release_time = moment(
          new Date(agentExtensionLogsData.release_time),
        )
          .utc()
          .format('YYYY-MM-DD HH:mm');

        setTimeModification({
          checkOutTime: check_out_time,
          releaseTime: release_time,
        });
      } else {
        setTimeModification({
          checkOutTime: '',
          releaseTime: '',
        });
      }
    } else {
      setTimeModification({
        checkOutTime: '',
        releaseTime: '',
      });
    }
  }, [agentExtensionLogsData]);

  useEffect(() => {
    if (
      caseData &&
      caseData.status !== 0 &&
      Object.keys(caseData?.callDetails).length !== 0
    ) {
      const currentCaseId = caseData?.callDetails?.caseId
        ? caseData?.callDetails?.caseId
        : '';
      setCaseId(currentCaseId);
    } else {
      const currentCaseId = withLocalStorage(STORAGE_CURRENT_CASE_ID);
      setCaseId(currentCaseId);
    }
  }, [caseData, withLocalStorage]);

  useEffect(() => {
    let extensionDialogTimeout1: any;
    let extensionDialogTimeout3: any;

    if (
      agentExtensionLogsData &&
      agentExtensionLogsData.extension &&
      agentExtensionLogsData.check_in_time &&
      agentExtensionLogsData.check_out_time &&
      agentExtensionLogsData.release_time
    ) {
      const checkOutDateTime = moment(
        new Date(agentExtensionLogsData.check_out_time),
      ).utc();

      const releaseDateTime = moment(
        new Date(agentExtensionLogsData.release_time),
      ).utc();

      // case 4
      if (!caseId && userExtension === agentExtensionLogsData.extension) {
        extensionDialogTimeout1 = setInterval(() => {
          const currentDateTimeCase4 = moment(new Date()).utc();

          if (
            currentDateTimeCase4 > checkOutDateTime &&
            currentDateTimeCase4 > releaseDateTime
          ) {
            setIsOpenExtensionDialog(true);
            setExtensionDialogCase('Case1');
          }
        }, 60000);

        extensionDialogTimeout3 = setInterval(() => {
          const currentDateTimeCase = moment(new Date()).utc();

          if (
            currentDateTimeCase > checkOutDateTime &&
            currentDateTimeCase < releaseDateTime &&
            !caseId
          ) {
            setIsOpenExtensionDialog(true);
            setExtensionDialogCase('Case3');
          }
        }, 60000);
      }
    }

    return () => {
      // clear timeout when the component unmounts
      clearInterval(extensionDialogTimeout1);
      clearInterval(extensionDialogTimeout3);
    };
  }, [agentExtensionLogsData, caseId, userExtension]);

  useEffect(() => {
    let bannerTimeout: any;
    let extensionDialogTimeout2: any;

    if (
      agentExtensionLogsData &&
      agentExtensionLogsData.extension &&
      agentExtensionLogsData.check_in_time &&
      agentExtensionLogsData.check_out_time &&
      agentExtensionLogsData.release_time
    ) {
      const currentDateTime = moment(new Date()).utc();

      const checkOutDateTime = moment(
        new Date(agentExtensionLogsData.check_out_time),
      ).utc();
      const releaseDateTime = moment(
        new Date(agentExtensionLogsData.release_time),
      ).utc();

      if (
        currentDateTime < checkOutDateTime &&
        currentDateTime < releaseDateTime &&
        userExtension === agentExtensionLogsData.extension
      ) {
        checkOutDateTime.subtract(10, 'minute');

        const notificationTime = checkOutDateTime.diff(currentDateTime);

        if (notificationTime > 0) {
          // case 6
          if (caseId) {
            bannerTimeout = setTimeout(() => {
              if (displayDateTimeModification) {
                const snackBaroptions: SnackBarNotificationOptions = {
                  type: 'info',
                  title: `Extension ${agentExtensionLogsData.extension} is currently assigned to you.
                    Your extension assignment expires at ${displayDateTimeModification.checkOutTime}.
                    Please logout of the CV Portal before ${displayDateTimeModification.releaseTime} unless you are actively working a case.`,
                  additionalInfo: (
                    <>
                      <h5>
                        If you wish to extend your shift, please follow the
                        steps below:
                      </h5>
                      <ol>
                        <li>Log out from CV portal</li>
                        <li>
                          Notify your supervisor to extend your extension
                          checkout time
                        </li>
                        <li>
                          Log back into the CV Portal once the checkout time is
                          extended.
                        </li>
                      </ol>
                    </>
                  ),
                };
                showNotification(snackBaroptions);
              }
            }, notificationTime);
          }
          // case 5
          else {
            extensionDialogTimeout2 = setTimeout(() => {
              setIsOpenExtensionDialog(true);
              setExtensionDialogCase('Case2');
            }, notificationTime);
          }
        }
      }
    }

    return () => {
      // clear timeout when the component unmounts
      clearTimeout(bannerTimeout);
      clearTimeout(extensionDialogTimeout2);
    };
  }, [
    agentExtensionLogsData,
    caseId,
    showNotification,
    displayDateTimeModification,
    userExtension,
  ]);

  const handleLogout = async (): Promise<boolean> => {
    auth.signOut();
    if (closeExtensionDialog) {
      closeExtensionDialog();
    }
    return true;
  };

  return (
    <>
      {userExtension &&
        userProfile &&
        userProfile.isComplete &&
        isOpenExtensionDialog &&
        extensionDialogCase && (
          <ExtensionDialog
            isOpen={isOpen}
            agentExtension={isOpenExtensionDialog ? userExtension : ''}
            closeExtensionDialog={
              isOpenExtensionDialog ? closeExtensionDialog : undefined
            }
            data-testid="extensionDialog"
          >
            {isOpenExtensionDialog && extensionDialogCase === 'Case1' && (
              <>
                <h5>
                  Your current Extension Release Timeframe has expired. If you
                  need to continue working, please follow the steps below:
                </h5>
                <ol>
                  <li>Log out from CV portal</li>
                  <li>
                    Request a new extension using the Agent extension request
                    portal.
                  </li>
                  <li>
                    Log back into the CV Portal once the new extension is
                    assigned
                  </li>
                </ol>
                <div>
                  <StyledButtonPrimary
                    onClick={handleLogout}
                    data-testid="okButton"
                  >
                    LogOut
                  </StyledButtonPrimary>
                </div>
              </>
            )}

            {isOpenExtensionDialog &&
              extensionDialogCase === 'Case2' &&
              agentExtensionLogsData &&
              displayDateTimeModification && (
                <>
                  <p>
                    Extension {agentExtensionLogsData.extension} is currently
                    assigned to you. Your extension assignment expired at{' '}
                    {displayDateTimeModification.checkOutTime}. Please logout of
                    the CV Portal before{' '}
                    {displayDateTimeModification.releaseTime} unless actively
                    working a case.
                  </p>
                  <h5>
                    If you wish to extend your shift, please follow the steps
                    below:
                  </h5>
                  <ol>
                    <li>Log out from CV portal</li>
                    <li>
                      Notify your supervisor to extend your extension checkout
                      time.
                    </li>
                    <li>
                      Log back into the CV Portal once the checkout time is
                      extended.
                    </li>
                  </ol>
                  <div>
                    <StyledButtonPrimary
                      onClick={handleLogout}
                      data-testid="okButton"
                    >
                      LogOut
                    </StyledButtonPrimary>
                  </div>
                </>
              )}

            {isOpenExtensionDialog && extensionDialogCase === 'Case3' && (
              <>
                <h5>
                  Your extension check out time has passed. If you wish to
                  extend your shift, please request that to your supervisor
                  extend the checkout time and try logging into the CV portal
                  again.
                </h5>
                <div>
                  <StyledButtonPrimary
                    onClick={handleLogout}
                    data-testid="okButton"
                  >
                    LogOut
                  </StyledButtonPrimary>
                </div>
              </>
            )}
          </ExtensionDialog>
        )}
    </>
  );
};
